import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { CircularProgress, useTheme } from "@mui/material";
import { t } from "i18next";
import SecurityClearanceErrorMessage from "./SecurityClearanceErrorMessage";

type LastSearchField = "passport" | "emirates" | null;

interface SecurityClearanceStatusMessageProps {
  isChecking: boolean;
  status: string;
  serial?: string;
  errorMessage?: string;
  lastSearchField?: LastSearchField;
  onSerialClick?: () => void;
  translationKey?: string;
}

const SecurityClearanceStatusMessage = ({
  isChecking,
  status,
  serial,
  errorMessage,
  onSerialClick,
  translationKey = "gatepass",
}: SecurityClearanceStatusMessageProps) => {
  const theme = useTheme();

  if (isChecking) {
    return (
      <div className="flex flex-row my-2 items-center">
        <CircularProgress
          size={16}
          className="mr-1"
          style={{ color: theme.palette.warning.main }}
        />
        <span className="text-sm" style={{ color: theme.palette.warning.main }}>
          {t(`${translationKey}.notifications.checking-passport`)}
        </span>
      </div>
    );
  }

  if (!status) {
    if (errorMessage) {
      return <SecurityClearanceErrorMessage message={errorMessage} />;
    }

    return (
      <div
        className="flex flex-row my-2 items-center"
        style={{ color: theme.palette.text.secondary }}
      >
        <span className="text-sm">
          {t(`${translationKey}.notifications.security-clearance-placeholder`)}
        </span>
      </div>
    );
  }

  if (status === "APPROVED" && serial) {
    return (
      <div className="flex flex-row my-2 items-center">
        <CheckCircleOutlineIcon className="mr-1" style={{ color: theme.palette.success.main }} />
        <button
          className="underline cursor-pointer font-medium text-sm hover:opacity-80"
          style={{ color: theme.palette.success.main }}
          onClick={onSerialClick}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onSerialClick?.();
            }
          }}
          tabIndex={0}
          aria-label={`View security clearance ${serial}`}
          type="button"
        >
          {t(`${translationKey}.notifications.security-clearance-approved`, {
            serial,
          })}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-row my-2 items-center" style={{ color: theme.palette.error.main }}>
      <HighlightOffIcon className="mr-1" />
      <p>{errorMessage || t(`${translationKey}.notifications.security-clearance-not-found`)}</p>
    </div>
  );
};

export default SecurityClearanceStatusMessage;
